// AddLaborDialog — Phase 4C
//
// Two safe modes:
//   - Standalone: creates a real jobs row + ticket_jobs row using a canonical
//     manual-labor catalog entry. Identical to Add Job at the persistence layer.
//   - Attached: adjusts the chosen ticket_jobs.minutes_override by a delta.
//
// AUTHORITY CONTRACT: ticket_jobs is the sole scheduler time authority. We
// recompute predicted_minutes via the canonical helper after every mutation.
// Any optional `notes` text is DISPLAY-ONLY metadata — never parsed as authority.

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, Timer } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { recomputeTicketPredictedMinutes } from '@/utils/recomputeTicketPredictedMinutes';
import { useAddonSuggestionTrigger } from '@/contexts/AddonSuggestionContext';
import { buildLaborColumnsForAutoWrite, refreshLaborForRowEdit } from '@/lib/labor/autoWriteLabor';
import { buildTechPayColumnsForAutoWrite, refreshTechPayForRowEdit } from '@/lib/labor/autoWriteTechPay';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';

interface AddLaborDialogProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  existingJobs: Array<{ id: string; name: string; standardMinutes: number }>;
}

type Mode = 'standalone' | 'attached';

const MANUAL_LABOR_CODES = [
  'MANUAL_DIAG_LABOR',
  'MANUAL_GEN_LABOR',
  'MANUAL_FAB_LABOR',
  'MANUAL_SETUP_LABOR',
] as const;

// Skill-level word → numeric tier (mirrors AddJobDialog).
const SKILL_TIER_MAP: Record<string, number> = {
  basic: 2,
  intermediate: 4,
  advanced: 7,
  expert: 9,
};

export function AddLaborDialog({
  isOpen,
  onClose,
  ticketId,
  existingJobs,
}: AddLaborDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // CORE-BP item 6. Standalone labor adds a job; attached labor edits one.
  const { evaluateAfterWorkAdded } = useAddonSuggestionTrigger();
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const techPayEnabled = useExposureFlag('ui_technician_pay');
  const [mode, setMode] = useState<Mode>('standalone');
  const [catalogRows, setCatalogRows] = useState<any[]>([]);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>('');
  const [attachedJobId, setAttachedJobId] = useState<string>('');
  const [minutes, setMinutes] = useState<string>('30');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Load the 4 canonical manual-labor catalog rows.
    (async () => {
      const { data, error } = await newJobCatalogQuery('id, label, code, minutes_default, skill_level, category_key, sub_menu, primary_bay_type, allowed_bay_types, required_capabilities, excluded_bay_types')
        .in('code', MANUAL_LABOR_CODES as unknown as string[])
        .order('label');
      if (error) {
        console.error('[AddLaborDialog] failed to load manual-labor catalog:', error);
        return;
      }
      setCatalogRows(data || []);
      if (data && data.length > 0 && !selectedCatalogId) {
        setSelectedCatalogId(data[0].id);
        setMinutes(String(data[0].minutes_default ?? 30));
      }
    })();
    // Reset form
    setMode('standalone');
    setAttachedJobId(existingJobs[0]?.id ?? '');
    setNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleConfirm = async () => {
    const mins = parseInt(minutes, 10);
    if (isNaN(mins) || mins <= 0) {
      toast({ title: 'Invalid minutes', description: 'Enter a positive number.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (mode === 'standalone') {
        const catalog = catalogRows.find((c) => c.id === selectedCatalogId);
        if (!catalog) {
          toast({ title: 'Pick a labor type', variant: 'destructive' });
          setSubmitting(false);
          return;
        }

        // Resolve shop_id from the ticket (avoid relying on first shop_profile row).
        const { data: ticketRow, error: tErr } = await supabase
          .from('tickets')
          .select('shop_id')
          .eq('id', ticketId)
          .single();
        if (tErr || !ticketRow) throw tErr ?? new Error('ticket not found');

        const skillTier = SKILL_TIER_MAP[(catalog.skill_level || '').toLowerCase()] ?? 4;

        // 1) Create the jobs row (per-shop instance of the catalog entry).
        const { data: jobData, error: jobErr } = await supabase
          .from('jobs')
          .insert({
            name: catalog.label,
            submenu: catalog.sub_menu,
            category: catalog.category_key,
            bay_requirement: 'Lift', // legacy column; real authority is the snapshot arrays below
            skill_level: skillTier,
            shop_id: ticketRow.shop_id,
            standard_minutes: catalog.minutes_default,
          })
          .select()
          .single();
        if (jobErr || !jobData) throw jobErr;

        // 2) Link to the ticket. minutes_override only if user changed from default.
        // Phase 10 R2: include sell-side labor columns when financial flags are ON.
        const effectiveMins = mins !== catalog.minutes_default ? mins : catalog.minutes_default;
        const laborCols = await buildLaborColumnsForAutoWrite({
          shopId: ticketRow.shop_id,
          effectiveMinutes: effectiveMins,
          jobCategory: catalog.category_key,
          flags: {
            labor_engine_enabled: laborEngineEnabled,
            ui_ticket_financials: ticketFinancialsEnabled,
          },
        });
        // Phase 10 R3: read assigned tech for this ticket → tech-pay snapshot.
        const { data: ticketTechRow } = await supabase
          .from('tickets')
          .select('assigned_tech_id')
          .eq('id', ticketId)
          .maybeSingle();
        const techPayCols = await buildTechPayColumnsForAutoWrite({
          shopId: ticketRow.shop_id,
          technicianId: ticketTechRow?.assigned_tech_id ?? null,
          referenceMinutes: effectiveMins,
          retailSoldMinutes: (laborCols as any)?.sold_minutes ?? null,
          flags: {
            labor_engine_enabled: laborEngineEnabled,
            ui_technician_pay: techPayEnabled,
          },
        });
        const { error: linkErr } = await supabase.from('ticket_jobs').insert({
          ticket_id: ticketId,
          job_id: jobData.id,
          minutes_override: mins !== catalog.minutes_default ? mins : null,
          notes: notes || null,
          created_by_user_id: user?.id,
          allowed_bay_types_snapshot: catalog.allowed_bay_types ?? [],
          required_capabilities_snapshot: catalog.required_capabilities ?? [],
          excluded_bay_types_snapshot: catalog.excluded_bay_types ?? [],
          bay_requirement_source: 'catalog',
          ...(laborCols ?? {}),
          ...(techPayCols ?? {}),
        });
        if (linkErr) throw linkErr;
      } else {
        // Attached mode: add `mins` to the chosen ticket_job's effective minutes.
        if (!attachedJobId) {
          toast({ title: 'Pick a job to attach to', variant: 'destructive' });
          setSubmitting(false);
          return;
        }

        // Read current effective minutes for this ticket_job (composite key).
        const { data: tjRow, error: tjErr } = await supabase
          .from('ticket_jobs')
          .select('minutes_override, notes, jobs:job_id(standard_minutes)')
          .eq('ticket_id', ticketId)
          .eq('job_id', attachedJobId)
          .single();
        if (tjErr || !tjRow) throw tjErr ?? new Error('ticket_job not found');

        const currentEffective =
          tjRow.minutes_override ?? (tjRow.jobs as any)?.standard_minutes ?? 0;
        const newEffective = currentEffective + mins;

        // Optional display-only sugar in notes — never parsed.
        const noteLine = notes
          ? `+${mins}m labor: ${notes}`
          : `+${mins}m labor`;
        const mergedNotes = tjRow.notes ? `${tjRow.notes}\n${noteLine}` : noteLine;

        const { error: updErr } = await supabase
          .from('ticket_jobs')
          .update({
            minutes_override: newEffective,
            notes: mergedNotes,
          })
          .eq('ticket_id', ticketId)
          .eq('job_id', attachedJobId);
        if (updErr) throw updErr;

        // Phase 10 R2: refresh labor sell on this row to match the new effective
        // minutes. Preserves override rate; no-op if uncomputed or flags off.
        const { data: ticketRowForLabor } = await supabase
          .from('tickets')
          .select('shop_id')
          .eq('id', ticketId)
          .single();
        if (ticketRowForLabor?.shop_id) {
          await refreshLaborForRowEdit({
            ticketId,
            jobId: attachedJobId,
            shopId: ticketRowForLabor.shop_id,
            effectiveMinutes: newEffective,
            flags: {
              labor_engine_enabled: laborEngineEnabled,
              ui_ticket_financials: ticketFinancialsEnabled,
            },
          }).catch((err) =>
            console.error('[AddLaborDialog] refreshLaborForRowEdit failed:', err),
          );
          // Phase 10 R3: refresh tech-pay snapshot for this row (no-op when flags
          // off, no tech, or row has tech_pay_status IS NULL).
          await refreshTechPayForRowEdit({
            ticketId,
            jobId: attachedJobId,
            shopId: ticketRowForLabor.shop_id,
            referenceMinutes: newEffective,
            flags: {
              labor_engine_enabled: laborEngineEnabled,
              ui_technician_pay: techPayEnabled,
            },
          }).catch((err) =>
            console.error('[AddLaborDialog] refreshTechPayForRowEdit failed:', err),
          );
        }
      }

      // Canonical recompute + cascade — same path Add Job uses.
      await recomputeTicketPredictedMinutes(ticketId, { userId: user?.id });

      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-jobs-for-parts', ticketId] });

      toast({
        title: 'Labor added',
        description:
          mode === 'standalone'
            ? `${mins}m of standalone labor added.`
            : `${mins}m added to existing job.`,
      });

      // CORE-BP item 6 — STANDALONE only. Standalone labor creates a new job on
      // the ticket, which is work being added. Attached mode adds minutes to a
      // job that is already there: that is editing existing work, and editing
      // is not an add-work trigger.
      if (mode === 'standalone') {
        await evaluateAfterWorkAdded(ticketId);
      }

      onClose();
    } catch (e: any) {
      console.error('[AddLaborDialog] failed:', e);
      toast({
        title: 'Failed to add labor',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canAttach = existingJobs.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="h-4 w-4" />
            Add Labor
          </DialogTitle>
          <DialogDescription>
            Standalone labor creates a manual-labor job. Attached labor adjusts an
            existing job's minutes. Either way, the scheduler stays in sync.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v as Mode)}
            className="grid grid-cols-2 gap-2"
          >
            <Label
              htmlFor="mode-standalone"
              className="flex items-center gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/50 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
            >
              <RadioGroupItem value="standalone" id="mode-standalone" />
              <span className="text-sm">Standalone</span>
            </Label>
            <Label
              htmlFor="mode-attached"
              className={`flex items-center gap-2 rounded-md border p-3 ${
                canAttach ? 'cursor-pointer hover:bg-muted/50' : 'opacity-50 cursor-not-allowed'
              } [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5`}
            >
              <RadioGroupItem value="attached" id="mode-attached" disabled={!canAttach} />
              <span className="text-sm">Attach to job</span>
            </Label>
          </RadioGroup>

          {mode === 'standalone' && (
            <div className="space-y-2">
              <Label>Labor type</Label>
              <Select
                value={selectedCatalogId}
                onValueChange={(v) => {
                  setSelectedCatalogId(v);
                  const row = catalogRows.find((c) => c.id === v);
                  if (row) setMinutes(String(row.minutes_default ?? 30));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose labor type" />
                </SelectTrigger>
                <SelectContent>
                  {catalogRows.map((row) => (
                    <SelectItem key={row.id} value={row.id}>
                      {row.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode === 'attached' && (
            <div className="space-y-2">
              <Label>Attach to existing job</Label>
              <Select value={attachedJobId} onValueChange={setAttachedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose job" />
                </SelectTrigger>
                <SelectContent>
                  {existingJobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name} ({j.standardMinutes}m)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="labor-minutes">
              {mode === 'standalone' ? 'Minutes' : 'Additional minutes'}
            </Label>
            <Input
              id="labor-minutes"
              type="number"
              min="1"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="labor-notes">Notes (optional)</Label>
            <Textarea
              id="labor-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was this labor for?"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding…
              </>
            ) : (
              'Add Labor'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
